import { api } from '@/shared'
import { message } from 'antd'
import { workLogActions } from '@/entity/workLog/index'
import { t } from 'i18next'
import { getFromSessionStorage, setToSessionStorage, removeFromSessionStorage } from '@/shared/lib/SessionStorage'
import { extractDatesFromWorkLogContent } from '@features/workLog/utils/dateUtils'
import { setToLocalStorage } from '@/shared/lib/LocalStorage'
import { handbookActions } from '@/entity/handbook'

export const getWorkLogData = () => {
	return async (dispatch) => {
		try {
			dispatch(workLogActions.getWorkLogAction())

			const res = await api.workLog.getWorkLog()

			if (res) {
				// Сохраняем признак isAllowed в localStorage если он есть в ответе
				if (res?.isAllowed !== undefined) {
					setToLocalStorage('worklog_isAllowed', res.isAllowed)
				}

				dispatch(workLogActions.getWorkLogActionDone(res))
			}
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))

			dispatch(workLogActions.getWorkLogActionFail())
		}
	}
}

export const createWorklog = (payload) => {
	const { data, onSuccess, onError, bin, deptCode } = payload

	return async (dispatch) => {
		try {
			const res = await api.workLog.createWorkLog(data)

			if (res) {
				// Добавляем работу в существующий workLog
				dispatch(
					workLogActions.addWorkToWorkLog({
						bin,
						deptCode,
						work: res,
					})
				)

				onSuccess(res)
			}
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))

			if (onError) {
				onError(error)
			}
		}
	}
}

export const updateWorklog = (payload) => {
	const { data, onSuccess, onError, bin, deptCode } = payload

	return async (dispatch) => {
		try {
			const res = await api.workLog.updateWorkLog(data)

			if (res) {
				// Обновляем работу в существующем workLog
				dispatch(
					workLogActions.updateWorkInWorkLog({
						bin,
						deptCode,
						work: res,
					})
				)

				onSuccess(res)
			}
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))

			if (onError) {
				onError(error)
			}
		}
	}
}

export const deleteRequest = (payload) => {
	const { data, onSuccess, bin, deptCode } = payload

	return async (dispatch) => {
		try {
			dispatch(workLogActions.deleteWorkInWorkLogAction())

			const res = await api.workLog.deleteWorklog({
				params: {
					externalId: data,
				},
			})

			onSuccess(res)
			dispatch(
				workLogActions.deleteWorkInWorkLogActionDone({
					bin,
					deptCode,
					workId: data,
				})
			)
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))
			dispatch(workLogActions.deleteWorkInWorkLogActionFail())
		}
	}
}

export const deleteDay = (payload) => {
	const { data, onSuccess, onError } = payload

	return async (dispatch) => {
		try {
			dispatch(workLogActions.deleteDayAction())

			const { date, bin, deptCode } = data

			const res = await api.workLog.deleteDay({
				params: {
					date: date,
					bin: bin,
					deptCode: deptCode,
				},
			})

			if (onSuccess) {
				onSuccess(res)
			}
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))
			dispatch(workLogActions.deleteDayActionFail())

			if (onError) {
				onError(error)
			}
		}
	}
}

/**
 * Получает справочник функций/работ
 * @param {string} bin - БИН организации
 * @param {string} deptCode - код департамента
 * @returns {Function} - thunk функция
 */
export const getWorklogFunctions = (bin, deptCode) => {
	return async (dispatch) => {
		try {
			const res = await api.handbook.getWorklogFunc({
				bin,
				deptCode,
			})

			if (res) {
				dispatch(
					handbookActions.setOptions({
						name: 'worklogFunc',
						data: res,
					})
				)
			}
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))
		}
	}
}

// UI Logic Thunks
export const initializeWorkLogOrg = (workLogContent) => (dispatch, getState) => {
	const { ui } = getState().workLog
	let targetOrgBin = ui.activeWorkLogOrg

	const storedActiveOrg = getFromSessionStorage('activeWorkLogOrg')
	if (!storedActiveOrg) {
		const firstOrg = workLogContent[0]
		setToSessionStorage('activeWorkLogOrg', firstOrg.bin)
		dispatch(workLogActions.setActiveWorkLogOrg(firstOrg.bin))
		targetOrgBin = firstOrg.bin
	} else {
		const orgExists = workLogContent.find((item) => item.bin === storedActiveOrg)
		if (orgExists) {
			dispatch(workLogActions.setActiveWorkLogOrg(storedActiveOrg))
			targetOrgBin = storedActiveOrg
		} else {
			const firstOrg = workLogContent[0]
			setToSessionStorage('activeWorkLogOrg', firstOrg.bin)
			dispatch(workLogActions.setActiveWorkLogOrg(firstOrg.bin))
			targetOrgBin = firstOrg.bin
		}
	}

	// Формируем массив заблокированных дат
	const disabled = extractDatesFromWorkLogContent(workLogContent, targetOrgBin)
	dispatch(workLogActions.setDisabledDates(disabled))

	// Инициализируем активную вкладку
	if (targetOrgBin) {
		const selectedOrg = workLogContent.find((org) => org.bin === targetOrgBin)
		if (selectedOrg?.employees?.length) {
			const firstEmployee = selectedOrg.employees[0]
			if (firstEmployee?.depts?.length && !ui.activeTab) {
				dispatch(workLogActions.setActiveTab(firstEmployee.depts[0].code))
			}
		}
	}

	// Автоматически выбираем первую дату
	if (disabled.length > 0 && !ui.selectedCarouselDate) {
		const sortedDates = disabled.sort((a, b) => b.getTime() - a.getTime())
		dispatch(workLogActions.setSelectedCarouselDate(sortedDates[0]))
	}
}

export const changeWorkLogOrg = (value, workLogContent) => (dispatch, getState) => {
	const { ui } = getState().workLog

	if (ui.activeWorkLogOrg !== value) {
		const item = workLogContent?.find((item) => item.bin === value)
		removeFromSessionStorage('activeWorkLogOrg')
		setToSessionStorage('activeWorkLogOrg', item.bin)
		dispatch(workLogActions.setActiveWorkLogOrg(item.bin))
		dispatch(workLogActions.resetOrgData())
	}
}

export const getCompanyAllowed = (payload = {}) => {
	const { onSuccess, onError } = payload

	return async (dispatch) => {
		try {
			dispatch(workLogActions.getCompanyAllowedAction())

			const res = await api.workLog.getCompanyAllowed()
			// Обновляем флаг доступности компании в сторе
			dispatch(workLogActions.getCompanyAllowedActionDone(res))

			if (onSuccess) {
				onSuccess(res)
			}
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))
			dispatch(workLogActions.getCompanyAllowedActionFail())

			if (onError) {
				onError(error)
			}
		}
	}
}

// Employer stage 1: fetch active employee list
export const getActiveEmployeeList = () => {
	return async (dispatch) => {
		try {
			dispatch(workLogActions.getActiveEmployeeListAction())

			const res = await api.workLog.getActiveEmployeeList()
			dispatch(workLogActions.getActiveEmployeeListActionDone(res))
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))
			dispatch(workLogActions.getActiveEmployeeListActionFail())
		}
	}
}

// Employer stage 1: fetch fired employee list
export const getFiredEmployeeList = () => {
	return async (dispatch) => {
		try {
			dispatch(workLogActions.getFiredEmployeeListAction())

			const res = await api.workLog.getFiredEmployeeList()
			dispatch(workLogActions.getFiredEmployeeListActionDone(res))
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))
			dispatch(workLogActions.getFiredEmployeeListActionFail())
		}
	}
}

// Employer stage 2: fetch employee dates
export const getEmployeeDates = (payload) => {
	const { data, onSuccess, onError } = payload

	return async (dispatch) => {
		try {
			dispatch(workLogActions.getEmployeeDatesAction())

			let body = {
				params: data,
			}

			const res = await api.workLog.getEmployeeDates(body)
			dispatch(workLogActions.getEmployeeDatesActionDone(res))

			if (onSuccess) {
				onSuccess(res)
			}
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))
			dispatch(workLogActions.getEmployeeDatesActionFail())

			if (onError) {
				onError(error)
			}
		}
	}
}

// Employer: работы за дату
export const getWorkByDate = (payload) => {
	const { data, onSuccess, onError } = payload

	return async (dispatch) => {
		try {
			dispatch(workLogActions.getWorkByDateAction())

			const res = await api.workLog.getWorkByDate({ params: data })
			dispatch(workLogActions.getWorkByDateActionDone(res))

			if (onSuccess) {
				onSuccess(res)
			}
		} catch (error) {
			message.error(error && error.response && error.response.data ? error.response.data.message : t('unknown_error'))
			dispatch(workLogActions.getWorkByDateActionFail())

			if (onError) {
				onError(error)
			}
		}
	}
}
